
import { supabase } from "@/integrations/supabase/client";
import { MembershipPaymentResult } from "../types/membership";

// Confirmer une adhésion après paiement
export async function confirmMembershipPayment(membershipId: string, paymentId: string) {
  try {
    console.log(`Confirmation du paiement pour l'adhésion avec paiement id=${paymentId}`);
    
    // Rechercher l'adhésion avec l'ID de paiement Stripe
    const { data: membershipData, error: fetchError } = await supabase
      .from("memberships")
      .select("id, first_name, last_name, email, membership_type, amount, payment_status, address, city, postal_code")
      .eq("stripe_payment_id", paymentId)
      .eq("payment_status", "pending")
      .maybeSingle();
    
    if (fetchError) {
      console.error("Erreur lors de la recherche de l'adhésion:", fetchError);
      throw new Error(`Erreur lors de la recherche de l'adhésion: ${fetchError.message}`);
    }
    
    if (!membershipData) {
      console.log("Adhésion non trouvée pour le paiement ID:", paymentId);
      
      // Essayer de trouver le membre par d'autres statuts en cas de problème de synchronisation
      const { data: completedData, error: completedError } = await supabase
        .from("memberships")
        .select("id, first_name, last_name, email, membership_type, amount, payment_status, address, city, postal_code")
        .eq("stripe_payment_id", paymentId)
        .eq("payment_status", "paid")
        .maybeSingle();
        
      if (completedData) {
        console.log("Adhésion déjà traitée avec statut 'paid':", completedData);
        return {
          success: true,
          membershipId: completedData.id,
          membershipData: completedData,
          message: "Paiement déjà confirmé"
        };
      }
      
      // Vérifier également avec le statut "completed" qui pourrait avoir été mis par le webhook
      const { data: webhookData, error: webhookError } = await supabase
        .from("memberships")
        .select("id, first_name, last_name, email, membership_type, amount, payment_status, address, city, postal_code")
        .eq("stripe_payment_id", paymentId)
        .eq("payment_status", "completed")
        .maybeSingle();
        
      if (webhookData) {
        console.log("Adhésion déjà traitée avec statut 'completed':", webhookData);
        return {
          success: true,
          membershipId: webhookData.id,
          membershipData: webhookData,
          message: "Paiement déjà confirmé par webhook"
        };
      }
      
      // Chercher par ID de paiement sans filtrer par statut
      const { data: anyData, error: anyError } = await supabase
        .from("memberships")
        .select("id, first_name, last_name, email, membership_type, amount, payment_status, address, city, postal_code")
        .eq("stripe_payment_id", paymentId)
        .maybeSingle();
        
      if (anyData) {
        console.log("Adhésion trouvée avec un statut différent:", anyData.payment_status);
        
        // Si on a trouvé une adhésion avec un autre statut, on la met à jour
        if (anyData.payment_status !== "paid" && anyData.payment_status !== "completed") {
          const { error: updateError } = await supabase
            .from("memberships")
            .update({ payment_status: "paid" })
            .eq("id", anyData.id);
            
          if (updateError) {
            console.error("Erreur lors de la mise à jour du statut:", updateError);
          } else {
            console.log("Statut de paiement mis à jour avec succès");
          }
        }
        
        // Même si l'adhésion est déjà traitée, on va quand même générer le CERFA et envoyer l'email pour être sûr
        await processDocumentsAndSendEmail(anyData);
        
        return {
          success: true,
          membershipId: anyData.id,
          membershipData: anyData,
          message: "Paiement confirmé et documents envoyés"
        };
      }
      
      // Dernière tentative: chercher par ID directement
      const { data: directData, error: directError } = await supabase
        .from("memberships")
        .select("id, first_name, last_name, email, membership_type, amount, payment_status, address, city, postal_code")
        .eq("id", membershipId)
        .maybeSingle();
        
      if (directData) {
        console.log("Adhésion trouvée par ID direct:", directData.id);
        
        // Mettre à jour le statut de paiement et l'ID de paiement Stripe
        const { error: directUpdateError } = await supabase
          .from("memberships")
          .update({ 
            payment_status: "paid",
            stripe_payment_id: paymentId
          })
          .eq("id", directData.id);
          
        if (directUpdateError) {
          console.error("Erreur lors de la mise à jour du statut par ID direct:", directUpdateError);
        } else {
          console.log("Statut de paiement mis à jour avec succès par ID direct");
        }
        
        // Générer les documents et envoyer l'email
        await processDocumentsAndSendEmail(directData);
        
        return {
          success: true,
          membershipId: directData.id,
          membershipData: directData,
          message: "Paiement confirmé par ID direct et documents envoyés"
        };
      }
      
      throw new Error("Adhésion non trouvée ou déjà payée");
    }
    
    // Vérifier si l'adresse est complète
    if (!membershipData.address || !membershipData.city || !membershipData.postal_code) {
      console.warn("Attention: Données d'adresse incomplètes pour le membre ID:", membershipData.id);
    }
    
    // Mise à jour du statut de l'adhésion
    const { error: updateError } = await supabase
      .from("memberships")
      .update({ 
        payment_status: "paid",
        stripe_payment_id: paymentId 
      })
      .eq("id", membershipData.id);
    
    if (updateError) {
      console.error("Erreur lors de la mise à jour du statut de paiement:", updateError);
      throw new Error(`Erreur lors de la mise à jour du statut de paiement: ${updateError.message}`);
    }
    
    // Traiter les documents et envoyer l'email
    await processDocumentsAndSendEmail(membershipData);
    
    return {
      success: true,
      membershipId: membershipData.id,
      membershipData: membershipData,
    };
  } catch (error) {
    console.error("Erreur lors de la confirmation du paiement:", error);
    throw error;
  }
}

// Fonction pour générer les documents et envoyer l'email
async function processDocumentsAndSendEmail(membershipData: any) {
  try {
    // Récupérer l'ID de signature de la charte pour cet utilisateur
    const { data: signatureData } = await supabase
      .from('charter_signatories')
      .select('id')
      .eq('email', membershipData.email)
      .eq('email_verified', true)
      .maybeSingle();
    
    const signatureId = signatureData?.id;
    console.log("ID de signature trouvé:", signatureId || "Non trouvé");
    
    // Générer le CERFA via la fonction dédiée
    try {
      console.log("Génération du CERFA pour l'adhésion:", membershipData.id);
      
      const cerfaResult = await supabase.functions.invoke("generate-cerfa", {
        body: {
          membershipId: membershipData.id,
          testData: {
            firstName: membershipData.first_name,
            lastName: membershipData.last_name,
            address: membershipData.address,
            postalCode: membershipData.postal_code,
            city: membershipData.city,
            amount: membershipData.amount,
            createdAt: new Date().toISOString()
          }
        }
      });
      
      if (cerfaResult.error) {
        console.error("Erreur lors de la génération du CERFA:", cerfaResult.error);
        throw cerfaResult.error;
      }
      
      console.log("CERFA généré avec succès:", cerfaResult.data);
      
      // Récupérer l'URL du CERFA généré
      const cerfaUrl = cerfaResult.data.cerfa_url;
      
      // Mettre à jour l'URL du CERFA dans la base
      if (cerfaUrl) {
        const { error: cerfaUpdateError } = await supabase
          .from("memberships")
          .update({ cerfa_url: cerfaUrl })
          .eq("id", membershipData.id);
        
        if (cerfaUpdateError) {
          console.error("Erreur lors de la mise à jour de l'URL du CERFA:", cerfaUpdateError);
        }
      }
      
      // Vérifier si c'est un don, auquel cas on n'inclut pas la charte
      const isDonor = membershipData.membership_type === "donor";
      const includeCharter = !isDonor; // Ne pas inclure la charte si c'est un donateur
      
      console.log(`Type d'adhésion: ${membershipData.membership_type}, Inclusion charte: ${includeCharter ? 'Oui' : 'Non'}`);
      
      // Envoi de l'email de confirmation avec CERFA et charte personnalisée uniquement si ce n'est pas un don
      console.log("Appel à la fonction send-cerfa-email avec les données:", {
        firstName: membershipData.first_name,
        lastName: membershipData.last_name,
        email: membershipData.email,
        membershipId: membershipData.id,
        cerfaUrl: cerfaUrl || null,
        signatureId: signatureId || undefined,
        includeCharter: includeCharter, // Ne pas inclure la charte pour les dons
        membershipType: membershipData.membership_type
      });
      
      const emailResult = await supabase.functions.invoke("send-cerfa-email", {
        body: {
          firstName: membershipData.first_name,
          lastName: membershipData.last_name,
          email: membershipData.email,
          membershipId: membershipData.id,
          cerfaUrl: cerfaUrl || null,
          signatureId: signatureId || undefined,
          includeCharter: includeCharter, // Ne pas inclure la charte pour les dons
          membershipType: membershipData.membership_type
        }
      });
      
      if (emailResult.error) {
        console.error("Erreur lors de l'envoi de l'email de confirmation:", emailResult.error);
        
        // Essayer une deuxième fois avec la fonction alternative
        console.log("Tentative avec send-membership-cerfa comme alternative...");
        const altEmailResult = await supabase.functions.invoke("send-membership-cerfa", {
          body: {
            firstName: membershipData.first_name,
            lastName: membershipData.last_name,
            email: membershipData.email,
            address: membershipData.address,
            city: membershipData.city,
            postalCode: membershipData.postal_code,
            membershipType: membershipData.membership_type,
            amount: membershipData.amount,
            includeCharter: includeCharter // Ne pas inclure la charte pour les dons
          }
        });
        
        if (altEmailResult.error) {
          console.error("Erreur lors de la tentative alternative d'envoi d'email:", altEmailResult.error);
          throw new Error(`Échec des deux tentatives d'envoi d'email: ${emailResult.error.message} et ${altEmailResult.error.message}`);
        } else {
          console.log("Email envoyé avec succès via la méthode alternative:", altEmailResult.data);
        }
      } else {
        console.log("Email de confirmation envoyé avec succès:", emailResult.data);
      }
    } catch (cerfaError) {
      console.error("Exception lors de la génération ou de l'envoi du CERFA:", cerfaError);
      // Tentative alternative en cas d'échec de la première méthode
      try {
        // Vérifier si c'est un don, auquel cas on n'inclut pas la charte
        const isDonor = membershipData.membership_type === "donor";
        const includeCharter = !isDonor; // Ne pas inclure la charte si c'est un donateur
        
        console.log("Tentative directe avec send-membership-cerfa suite à une erreur...");
        const directEmailResult = await supabase.functions.invoke("send-membership-cerfa", {
          body: {
            firstName: membershipData.first_name,
            lastName: membershipData.last_name,
            email: membershipData.email,
            address: membershipData.address,
            city: membershipData.city,
            postalCode: membershipData.postal_code,
            membershipType: membershipData.membership_type,
            amount: membershipData.amount,
            includeCharter: includeCharter // Ne pas inclure la charte pour les dons
          }
        });
        
        if (directEmailResult.error) {
          console.error("Échec de la tentative directe:", directEmailResult.error);
        } else {
          console.log("Email envoyé avec succès via la méthode directe:", directEmailResult.data);
        }
      } catch (directError) {
        console.error("Exception lors de la tentative directe:", directError);
      }
    }
  } catch (processingError) {
    console.error("Erreur lors du traitement des documents et de l'envoi d'email:", processingError);
    throw processingError;
  }
}
